/**
 * The three ways to say how long Offline lasts.
 *
 * Worked out on the device rather than on the server because "tomorrow morning"
 * belongs to the person pressing the button: the moment is sent as an instant,
 * and the server only checks that it can honour it. In this package because the
 * CRM and the Android app both offer the same three choices. No imports, so the
 * Android app can read this file directly.
 */
export type OfflineChoice = "hour" | "tomorrow" | "manual";

export const OFFLINE_CHOICES: readonly OfflineChoice[] = ["hour", "tomorrow", "manual"];

/** The default, so a forgotten switch cannot silence somebody for days. */
export const DEFAULT_OFFLINE_CHOICE: OfflineChoice = "tomorrow";

/** The hour "tomorrow morning" means, on the device's own clock. */
const MORNING_HOUR = 8;

/** The instant Offline lapses, or null for "until I turn it back on". */
export function offlineUntil(choice: OfflineChoice, now: Date = new Date()): string | null {
  if (choice === "manual") return null;
  if (choice === "hour") return new Date(now.getTime() + 60 * 60 * 1000).toISOString();
  const morning = new Date(now);
  morning.setDate(morning.getDate() + 1);
  morning.setHours(MORNING_HOUR, 0, 0, 0);
  return morning.toISOString();
}

/** The expiry for a label: only the clock today, the weekday as well after that. */
export function offlineUntilLabel(iso: string, locale: string, now: Date = new Date()): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  const sameDay = at.toDateString() === now.toDateString();
  return at.toLocaleString(locale, {
    ...(sameDay ? {} : { weekday: "short" }),
    hour: "2-digit",
    minute: "2-digit",
  });
}
